import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';

/**
 * The tenant identifier from the URL (`/veterans-case-mgmt/:entitySlug/...`). Local
 * alias replacing the removed `@opengov/bedrock` `EntitySlug` brand type.
 */
type EntitySlug = string;

/**
 * Hook that provides navigation helpers with proper app name and entity slug prefixes.
 * This ensures all navigation includes the required URL structure: /app-name/entity-slug/...
 */
export function useAppNavigation() {
  const navigate = useNavigate();
  const { entitySlug } = useParams<{ entitySlug: EntitySlug }>();
  const appName = 'veterans-case-mgmt';

  /**
   * Navigate to a path within the current app and entity context.
   * Automatically prepends the app name and entity slug.
   *
   * @param path - The path relative to /app-name/entity-slug (e.g., 'todos', 'todos/new')
   */
  const navigateToPath = useCallback(
    (path: string) => {
      const fullPath = `/${appName}/${entitySlug}/${path.replace(/^\//, '')}`;
      navigate(fullPath);
    },
    [navigate, entitySlug, appName],
  );

  /**
   * Navigate back one level in the current route hierarchy.
   */
  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /**
   * Build a full path with app name and entity slug prefix.
   *
   * @param path - The path relative to /app-name/entity-slug
   * @returns The full path including app name and entity slug
   */
  const buildPath = useCallback(
    (path: string) => {
      return `/${appName}/${entitySlug}/${path.replace(/^\//, '')}`;
    },
    [entitySlug, appName],
  );

  return {
    navigateToPath,
    navigateBack,
    buildPath,
    entitySlug,
    appName,
  };
}
